import React, {Dispatch, SetStateAction, useEffect, useState} from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import CloseIcon from '@mui/icons-material/Close';
import {DialogActions, FormControl, IconButton, InputLabel, MenuItem, Select, TextField} from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import {yupResolver} from '@hookform/resolvers/yup';
import {useForm} from 'react-hook-form';
import {ICreateLeadImplant} from '../reducers/LeadImplantReducers';
import {leadImplantSchema} from '../../../schemas';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../store';
import {ICompany} from '../../center/reducers';
import {ILeadReference} from '../../protheses/reducers';
import {Save as SaveIcon} from '@mui/icons-material';
import {createLeadImplant} from '../actions/leadImplantActions';
import Loader from '../../common/Loader';
import {toast} from 'react-toastify';

interface IProps {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  patientId: number | undefined
}
const ModalLeadImplant = (props: IProps) => {
  const [isLoading, setIsLoading] = useState(false) //eslint-disable-line
  const [implantDate, setImplantDate] = useState('')
  const leadReferenceList: ILeadReference[] = useSelector((state: RootState) => state.prosthesisReducer.leadsReferences);
  const makerList: (ICompany )[] = [{id:0, companyName: null, address: null, zipCode: null, city: null, countryId: null, phone: null, type: null, Enum: null, logoFileId: null}, ...useSelector((state: RootState) => state.centersReducer.makers).data];
  const [location, setLocation] = useState<'OREILLETTE_DROITE' | 'VENTRICULE_DROIT' | 'SINUS_CORONAIRE'>('OREILLETTE_DROITE')
  const [selectedMakerId, setSelectedMakerId] = useState<number | undefined>(undefined)
  const [selectedLeadReferenceId, setSelectedLeadReferenceId] = useState<number | undefined>(undefined)
  const [leadReferenceFiltered, setLeadReferenceFiltered] = useState(leadReferenceList)
  
  const dispatch = useDispatch()
  
  const handleClose = () => {
    props.setIsOpen(false)
  }
  
  const {handleSubmit, register, formState: {errors, isValid}} = useForm<ICreateLeadImplant>({
    resolver: yupResolver(leadImplantSchema),
  })
  
  const handleSave = async (values: ICreateLeadImplant) => {
    new Promise((resolve, reject) => {
      setIsLoading(true)
      dispatch(
        createLeadImplant(
          {
            ...values, patient_id: props.patientId, implant_date: implantDate
          },
          resolve,
          reject,
        ),
      )
    })
    .then(() => {
      handleClose()
    }).catch((err) => {
      console.error(`handleSave failed ${JSON.stringify(err)}`)
      toast.error("Erreur lors de l'ajout de l'implantation")
    }).finally(() => {
      setIsLoading(false)
    }
    )
  }
  
  useEffect(() => {
    setLeadReferenceFiltered([...leadReferenceList.filter((leadReference) => leadReference.maker_id === selectedMakerId)])
  }, [selectedMakerId, leadReferenceList])
  
  return (
      <Dialog
          fullWidth={true}
          maxWidth={'sm'}
          open={props.isOpen}
          onClose={handleClose}
          aria-labelledby="responsive-dialog-title"
          sx={{ padding: '30px auto' }}
          PaperProps={{
            style: { borderRadius: 20 },
          }}
      >
        <Loader
            text="Enregistrement de la nouvelle implantation"
            open={isLoading}
        />
        <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
        >
          <DialogTitle
              id="responsive-dialog-title"
              sx={{
                display: 'flex',
                position: 'relative',
                justifyContent: 'center',
                background: 'linear-gradient(60deg, #84c2ce, #96dcea)',
                color: 'white',
                fontWeight: '700',
                fontSize: '24px',
                padding: '10px 0',
              }}
          >
            <span>Sonde</span>
            <IconButton
                sx={{ position: 'absolute', right: '20px' }}
                onClick={handleClose}
            >
              <CloseIcon sx={{ color: 'white' }} />
            </IconButton>
          </DialogTitle>
          <DialogContent
              sx={{
                display: 'flex',
                alignItems: 'start',
                flexDirection: 'column',
                width: '100%',
              }}
          >
            <form onSubmit={handleSubmit(handleSave)} style={{width: '100%'}}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: '20px',
                width: '100%'
              }}>
                <FormControl sx={{ height: '100%', width: '100%' }}>
                    <InputLabel
                        id="localisation_select_label"
                    >
                      Localisation
                    </InputLabel>
                    <Select
                        {...register('lead_location')}
                        fullWidth
                        value={location}
                        name='lead_location'
                        labelId="localisation_select_label"
                        label="Localisation"
                        onChange={(event) => {setLocation(event.target.value as 'OREILLETTE_DROITE' | 'VENTRICULE_DROIT' | 'SINUS_CORONAIRE')}}
                    >
                      <MenuItem value={'OREILLETTE_DROITE'}>Oreillette droite</MenuItem>
                      <MenuItem value={'VENTRICULE_DROIT'}>Ventricule droit</MenuItem>
                      <MenuItem value={'SINUS_CORONAIRE'}>Sinus coronaire</MenuItem>
                    </Select>
                </FormControl>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: '20px',
              }}>
                <FormControl fullWidth>
                  <TextField {...register('serial_number')}
                             fullWidth
                             name="serial_number"
                             label="Numéro de série"
                             helperText={errors.serial_number?.message} />
                </FormControl>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: '20px',
              }}>
                <FormControl fullWidth>
                  <TextField
                             fullWidth
                             onChange={(event) => setImplantDate(event.target.value)}
                             type="date"
                             name="implant_date"
                             label="Date d'implantation"
                             helperText={errors.implant_date?.message} />
                </FormControl>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: '20px',
              }}>
                <FormControl fullWidth>
                  <InputLabel
                      id="maker_select_label"
                  >
                    Marque de la sonde
                  </InputLabel>
                  <Select
                    value={selectedMakerId}
                    labelId="maker_select_label"
                    label="Marque de la sonde"
                    onChange={(event) => {
                      console.log('selected maker id changed', event.target.value)
                      setSelectedMakerId(Number(event.target.value))
                      setSelectedLeadReferenceId(undefined)
                    }}
                  >
                    {makerList.map((maker: ICompany | undefined) => (
                        <MenuItem key={maker?.id} value={maker?.id}>{maker?.companyName}</MenuItem>
                    ))}
                  </Select>
                  
                </FormControl>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: '20px',}}>
                <FormControl fullWidth error={!!errors.reference_id}>
                  <InputLabel
                      id="lead_reference_select_label"
                  >
                    Modèle de sonde
                  </InputLabel>
                  <Select
                      {...register('reference_id')}
                      value={selectedLeadReferenceId}
                      labelId="lead_reference_select_label"
                      label="Modèle de sonde"
                      name={"reference_id"}
                      onChange={(event) => {
                        console.log('selected lead reference id changed', event.target.value)
                        setSelectedLeadReferenceId(Number(event.target.value))
                      }}
                  >
                    {leadReferenceFiltered.map((leadReference: ILeadReference) => (
                        <MenuItem key={leadReference.id} value={leadReference.id}>{leadReference.reference} - {leadReference.description} - {leadReference.lead_connection.lead_type} - {leadReference.lead_connection.lead_connection } - {leadReference.length} mm</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </form>
          </DialogContent>
          <DialogActions>
            <IconButton
                onClick={handleSubmit(handleSave)}
                sx={{
                  alignSelf: 'right',
                  color: isValid ? '#40D172' : 'default',
                }} // Conditional styling
            >
              <SaveIcon
                  sx={{
                    fontSize: '36px',
                  }}
              />
            </IconButton>
          </DialogActions>
        </div>
      </Dialog>
  )
}

export default ModalLeadImplant
